// Shared presentational pieces for the July-14 role Home dashboards (FR-P1/FR-P2). Content only —
// rendered inside the existing top-nav shell (the sidebar in the mockups is out of scope for this
// order; the dashboard CONTENT lives at the Home route).
import { Link } from "react-router-dom";
import { preprintStatusLabel, type DashboardPreprintCard } from "@rrid/shared-types";
import { Badge, Card, CardBody, CardHeader, EmptyState, statusTone } from "../../components/ui.js";

/** A single KPI stat card: big value + label + optional sublabel/delta. */
export function KpiCard({
  label,
  value,
  sublabel,
  tone = "slate",
}: {
  label: string;
  value: string | number;
  sublabel?: string;
  tone?: "slate" | "green" | "blue" | "amber" | "violet" | "red";
}): JSX.Element {
  const valueColor: Record<string, string> = {
    slate: "text-slate-800",
    green: "text-green-700",
    blue: "text-blue-700",
    amber: "text-amber-700",
    violet: "text-violet-700",
    red: "text-rose-700",
  };
  return (
    <Card className="h-full">
      <CardBody className="space-y-1">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
        <div className={`text-3xl font-bold ${valueColor[tone]}`}>{value}</div>
        {sublabel && <div className="text-xs text-slate-500">{sublabel}</div>}
      </CardBody>
    </Card>
  );
}

/** A titled column of preprint cards (the "My Preprints" kanban lists). */
export function PreprintColumn({
  title,
  cards,
  tone = "slate",
  showGreenlits = false,
  showCounts = false,
  emptyLabel = "Nothing here yet.",
}: {
  title: string;
  cards: DashboardPreprintCard[];
  tone?: "slate" | "green" | "blue" | "amber" | "violet";
  showGreenlits?: boolean;
  /** WO-7/22 Track A: team-level "proposed X · greenlit Y" under each card (active/mini-team). */
  showCounts?: boolean;
  emptyLabel?: string;
}): JSX.Element {
  return (
    <Card className="h-full">
      <CardHeader className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        <div className="flex items-center gap-2">
          {showGreenlits && <span className="text-[10px] uppercase tracking-wide text-slate-400">Greenlits</span>}
          <Badge tone={tone}>{cards.length}</Badge>
        </div>
      </CardHeader>
      <CardBody className="space-y-1.5">
        {cards.length === 0 ? (
          <p className="text-xs text-slate-400">{emptyLabel}</p>
        ) : (
          cards.map((c) => (
            <PreprintRow
              key={c.preprintId ?? c.selectedId ?? c.title}
              card={c}
              showGreenlits={showGreenlits}
              showCounts={showCounts}
            />
          ))
        )}
      </CardBody>
    </Card>
  );
}

/**
 * SoftR-parity R-003: the combined To-Pitch column — Team (blue) and Editorial (amber) pitches
 * stacked under one header so both pitch queues read as a single stage with distinct colors.
 */
export function PitchGroup({
  team,
  editorial,
}: {
  team: DashboardPreprintCard[];
  editorial: DashboardPreprintCard[];
}): JSX.Element {
  return (
    <Card className="h-full">
      <CardHeader className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">To-Pitch</h3>
        <Badge tone="slate">{team.length + editorial.length}</Badge>
      </CardHeader>
      <CardBody className="space-y-3">
        <PitchSublist label="Team" tone="blue" cards={team} />
        <PitchSublist label="Editorial" tone="amber" cards={editorial} />
      </CardBody>
    </Card>
  );
}

function PitchSublist({
  label,
  tone,
  cards,
}: {
  label: string;
  tone: "blue" | "amber";
  cards: DashboardPreprintCard[];
}): JSX.Element {
  const accent = tone === "blue" ? "border-l-blue-400" : "border-l-amber-400";
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Badge tone={tone}>{label}</Badge>
        <span className="text-xs text-slate-400">{cards.length}</span>
      </div>
      {cards.length === 0 ? (
        <p className="text-xs text-slate-400">No {label.toLowerCase()} pitches.</p>
      ) : (
        <div className={`space-y-1.5 border-l-2 pl-2 ${accent}`}>
          {cards.map((c) => (
            <PreprintRow key={c.preprintId ?? c.selectedId ?? c.title} card={c} showGreenlits={false} />
          ))}
        </div>
      )}
    </div>
  );
}

function PreprintRow({
  card,
  showGreenlits,
  showCounts = false,
}: {
  card: DashboardPreprintCard;
  showGreenlits: boolean;
  showCounts?: boolean;
}): JSX.Element {
  const to = card.selectedId ? `/preprints/${card.selectedId}` : card.preprintId ? `/reviewers` : "#";
  // WO-7/23 Track D: a mini-team in week 2 carries the hazard right on its card.
  const expiring = card.miniTeamPhase === "expiring";
  const inner = (
    <div className={`rounded-md border p-2 hover:border-brand ${expiring ? "border-orange-300 bg-orange-50" : "border-slate-200"}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-sm">{card.title}</span>
        {showGreenlits ? (
          <span className="shrink-0 text-sm font-semibold text-green-700">{card.greenlitCount ?? 0}</span>
        ) : (
          <Badge tone={statusTone(card.status)}>{preprintStatusLabel(card.status)}</Badge>
        )}
      </div>
      {/* WO-7/23 Track A: mini-teams sit inside Active — badge them so the origin stays clear. */}
      {card.isMiniTeam && (
        <p className="mt-0.5 flex flex-wrap items-center gap-1">
          <Badge tone="violet">Mini team</Badge>
          {expiring && <Badge tone="amber">⚠️ Expiring — propose now</Badge>}
        </p>
      )}
      {/* WO-7/22 Track A: team-level progress on active/mini-team cards. */}
      {showCounts && (
        <p className="mt-0.5 text-xs text-slate-500">
          proposed {card.proposedCount ?? 0} · <span className="font-medium text-green-700">greenlit {card.greenlitCount ?? 0}</span>
        </p>
      )}
    </div>
  );
  return to === "#" ? inner : <Link to={to}>{inner}</Link>;
}

export { EmptyState };
